import React, {useEffect, useRef, useState} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import NavItemPositionModalButton from "./navItemPositionModalButton";
import {toggleNavItemPositionModal} from "../../../actions/navigationFunctions";
import {CORNER_SHELVES_POSITIONS, PAN_POSITIONS, SIDE_POSITIONS} from "../../bvSceneComponent/core/constants";
// import {SLAT_WALL_POSITIONS} from "../../commonVisualizer/core/constants";
import {getCurrentLayout} from "../../appLayout";
import {featureInterface} from "../../../interfaces/featureInterface";
import NavItemPositionModalSlatWall from "./navItemPositionModalSlatWall";
import {GRAB_BARS_POSITIONS} from "../../look/core/constants";
import {useAppSelector} from "../../../storeHooks";
// import NavItemPositionModalSlatWallButton from "./navItemPositionModalSlatWallButton";

const disallowBlurClasses = [".nav-pos", ".feature", ".menu-list__item", ".m-feature"];

// current hash route without the query string, e.g. "#/materials/1?foo=1" -> "/materials/1"
const getRoutePath = () => (window.location.hash || "").replace(/^#/, "").split("?")[0] || "/";

export const getPositionsArray = (partId) => {
  switch (partId) {
    case "corner_shelf":
      return CORNER_SHELVES_POSITIONS
    case "grab_bar":
    case "shampoo_shelf":
      return document.kvConfig.visualizerType === "bvScene" ? PAN_POSITIONS : GRAB_BARS_POSITIONS
    // case "slat_wall":
    //   return SLAT_WALL_POSITIONS
    case "shower_pan_combo":
    case "shower_fixture":
    case "soap_dish":
      return PAN_POSITIONS
    default:
      return document.kvConfig.visualizerType === "bvState" ? SIDE_POSITIONS : ["L","R", "C"]
      // return ["L","R", "C"]
  }
}

const GRAB_BAR_SIDE_WALLS = GRAB_BARS_POSITIONS.slice(3, 5);    // LV, RV
const GRAB_BAR_BATHTUB_WALL = GRAB_BARS_POSITIONS.slice(5, 8);  // BL, BC, BR

/**
 * Grab bars / shampoo shelves: "Center Wall" (L, C, R) + optional "Side Walls" and "Bathtub Wall" rows.
 * The extra rows keep a fixed slot per position (null = empty cell) so the buttons line up.
 */
const getGrabBarRows = (isAvailable) => {
  const toSlots = positions => positions.map(pos => isAvailable(pos) ? pos : null);
  const hasSideWalls = GRAB_BAR_SIDE_WALLS.some(isAvailable);
  const hasBathtubWall = GRAB_BAR_BATHTUB_WALL.some(isAvailable);

  return [
    {title: hasSideWalls ? "Center Wall" : "Side", positions: PAN_POSITIONS.filter(isAvailable)},
    hasSideWalls && {title: "Side Walls", positions: toSlots(GRAB_BAR_SIDE_WALLS)},
    hasBathtubWall && {title: "Bathtub Wall", positions: toSlots(GRAB_BAR_BATHTUB_WALL)},
  ].filter(Boolean);
};

const NavItemPositionModal = (props) => {

  const {navItemPositionModal, toggleNavItemPositionModal, activePartId, windowWidth, windowHeight, isWizard,
    activeFeature} = props;
  const isMobile = useAppSelector(state => state.serverState.isMobile);
  const desktopNavigationOpened = useAppSelector(state => state.navigationState.desktopNavigationOpened);
  const bvOpened = useAppSelector(state => state.bathroomVisualizerState.bvOpened);
  const [parent, setParent] = useState(null);
  const [checkboxesUpdated, setCheckboxesUpdated] = useState(true);
  const left = navItemPositionModal.popupPosition.left;
  const top = navItemPositionModal.popupPosition.top;
  // read inside the unmount cleanup below, which must not close over a stale value
  const popupOpenedRef = useRef(navItemPositionModal.popupOpened);
  popupOpenedRef.current = navItemPositionModal.popupOpened;
  // last route this instance rendered on; on unmount it still holds the route we were living on,
  // while getRoutePath() already returns the new one when a navigation is what unmounted us
  const routePathRef = useRef(getRoutePath());
  routePathRef.current = getRoutePath();

  const contextMenuBlur = (e) => {
    if ((!disallowBlurClasses.some(cls => e.target.closest(cls)) || e.target.closest(".feature-buttons"))
      && navItemPositionModal.popupOpened) {
      toggleNavItemPositionModal({
        popupOpened: false,
        activePartId: ""
      })
    }
  };

  /**
   * Outside-click closing, registered in the CAPTURE phase on purpose.
   *
   * React 17 attaches its synthetic event listeners to the app root container, not to
   * `document`, so a bubbling listener here runs *after* the clicked element's own onClick.
   * For any button that navigates (e.g. `.materials-button--cart` -> `history.push("/shoppingcart")`)
   * that onClick synchronously unmounts the page that renders this modal, React flushes the
   * unmount + this effect's cleanup, and the listener is gone by the time the event would have
   * reached `document` — so the popup never got the click and never closed.
   *
   * Capture runs before every React handler, so the outside click is always seen first.
   */
  useEffect(() => {
    document.addEventListener('click', contextMenuBlur, true);
    return () => {
      document.removeEventListener('click', contextMenuBlur, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navItemPositionModal]);

  /**
   * `popupOpened` lives in redux, so it outlives this component: leaving /materials for
   * /shoppingcart only unmounts the modal, and coming back re-rendered it at its old
   * coordinates. Reset it when we unmount *because of a route change* — deliberately not on
   * every unmount, since the same popup is handed over between the page-level instance and the
   * visualizer one when `bvOpened` flips, and that hand-over must not close it.
   */
  useEffect(() => {
    const closeIfRouteChanged = () => {
      if (popupOpenedRef.current && getRoutePath() !== routePathRef.current) {
        toggleNavItemPositionModal({
          popupOpened: false,
          activePartId: ""
        })
      }
    };

    // covers route changes that do NOT unmount us (browser back/forward, links that keep the
    // page mounted); the cleanup covers the ones that do (e.g. the cart button)
    window.addEventListener("hashchange", closeIfRouteChanged);
    return () => {
      window.removeEventListener("hashchange", closeIfRouteChanged);
      closeIfRouteChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const layout = getCurrentLayout();
    setParent(layout.getFeatureGroupByPartId(activePartId))
  }, [activePartId]);

  const updateCheckboxes = () => {
    setCheckboxesUpdated(!checkboxesUpdated);
  };

  useEffect(() => {
    updateCheckboxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left, top, activePartId]);

  if (!navItemPositionModal.popupOpened || !parent) { // added !parent because the buttons were transitioning the modal width after the parent is set
    return null
  }

  if (!isMobile && !desktopNavigationOpened && bvOpened) {
    return null
  }

  const maxRight = left > windowWidth - 200;
  const fixedTop = Math.min(top, windowHeight - 130); // top > (windowHeight - 130) ? windowHeight - 130 : top;

  const styles = isMobile ? {
    left: maxRight ? "auto" : left + "px",
    top: fixedTop + "px",
    right: maxRight ? 0 : "auto"
  } : {
    transform: `translate(${maxRight ? left - 200 : left}px, ${fixedTop}px)`,
  }

  const triangleStyles = isMobile ? {
    left: left + "px",
    top: fixedTop + "px",
    position: "fixed",
    marginTop: 10,
  } : maxRight ? {
    left: "auto",
    right: "40px"
  } : {}

  const buttonProps = {parent, activeFeature, updateCheckboxes, checkboxesUpdated};

  if (activePartId === "slat_wall") {
    return <NavItemPositionModalSlatWall styles={styles}
                                         triangleStyles={triangleStyles}
                                         isWizard={isWizard}
                                         {...buttonProps}/>
  }

  const isShelf = activePartId === "corner_shelf";
  const isGrabBar = activePartId === "grab_bar" || activePartId === "shampoo_shelf";

  const positionsArray = getPositionsArray(activePartId)
    .filter(pos => parent.getMatchingPositionFeature(pos, activeFeature));
  const isAvailable = pos => positionsArray.includes(pos);

  const rows = isGrabBar
    ? getGrabBarRows(isAvailable)
    : [{title: isShelf ? "Left" : "Side", positions: positionsArray.slice(0, 3)}];

  if (isShelf && positionsArray.length > 3) {
    rows.push({title: "Right", positions: positionsArray.slice(3, 6)});
  }

  const firstLineLength = rows[0].positions.length;
  // const hasThreeOptionsGrabBar = rows.length > 1 && firstLineLength === 3;

  const className = classNames("nav-pos", {
    // "nav-pos--shelf": positionsArray.length > 3 && isShelf,
    "nav-pos--three": positionsArray.length === 3,
    "nav-pos--wizard": isWizard,
  });

  return (
    <div className={className} style={styles}>
      <span className="nav-pos__triangle" style={triangleStyles}/>
      <div className="nav-pos__content">
        {rows.map((row, rowIndex) => {
          const shouldJustifyBetween =
            rowIndex === 0
              ? firstLineLength === 2
              : isGrabBar && (firstLineLength < 3 || row.positions?.length < 3);
          return (
            (
              <div key={row.title}
                   className={rowIndex === 0 ? "nav-pos__content-top" : "nav-pos__content-bottom"}>
                <h4 className="nav-pos__title bold">{row.title}</h4>
                <div className={classNames("nav-pos__buttons !flex items-center u-gap-1", {
                  // first row spreads out when it has 2 buttons; grab bar extra rows spread out when the first row isn't full
                  "justify-between": shouldJustifyBetween
                })}>
                  {row.positions.map((position, index) => position ? (
                    <div className="flex flex--center flex--start" key={index}>
                      {position && <NavItemPositionModalButton position={position} {...buttonProps}/>}
                    </div>
                  ) : null)}
                </div>
              </div>
            )
          )
        })}
      </div>
    </div>
  )
};

NavItemPositionModal.propTypes = {
  // feature: PropTypes.object.isRequired
  activeFeature: PropTypes.shape(featureInterface),
  isWizard: PropTypes.bool
}

const mapStateToProps = (store, ownProps) => {
  const {windowHeight, windowWidth} = store.serverState;
  return {
    navItemPositionModal: store.navigationState.navItemPositionModal,
    activeFeature: ownProps.activeFeature || store.selectionState.activeFeature,
    activePartId: store.navigationState.navItemPositionModal.activePartId,
    windowHeight,
    windowWidth,
  };
};

const mapDispatchToProps = {
  toggleNavItemPositionModal
};

export default connect(mapStateToProps, mapDispatchToProps)(NavItemPositionModal)
